export interface QuestionnairePolicyQuestion {
  questionId: string;
  questionKey: string;
  applicable: boolean;
  scored: boolean;
  fixedScore: number | null;
  optionCodes?: string[];
}

export type QuestionnairePolicyQuestionIdentity = Pick<QuestionnairePolicyQuestion, 'questionId' | 'questionKey'>;

export interface QuestionnairePolicyEvaluation {
  questions: QuestionnairePolicyQuestion[];
  valid: boolean;
  errors: Array<{ questionKey: string; code: string }>;
  advisories: Array<{ ruleId: string; message: string }>;
}

// Validate the boundary; an unreadable evaluation must never render as "all applicable".
export function readQuestionnairePolicyEvaluation(value: unknown, expectedQuestions?: readonly QuestionnairePolicyQuestionIdentity[]): QuestionnairePolicyEvaluation {
  if (!value || typeof value !== 'object') throw new Error('questionnaire_policy_result_invalid');
  const result = value as Partial<QuestionnairePolicyEvaluation>;
  if (!Array.isArray(result.questions) || typeof result.valid !== 'boolean'
    || !Array.isArray(result.errors) || !Array.isArray(result.advisories)
    || result.valid !== (result.errors.length === 0)
    || result.errors.some((error) => !error || typeof error !== 'object' || Array.isArray(error)
      || typeof error.questionKey !== 'string' || typeof error.code !== 'string')
    || result.advisories.some((advisory) => !advisory || typeof advisory !== 'object' || Array.isArray(advisory)
      || typeof advisory.ruleId !== 'string' || typeof advisory.message !== 'string')
    || result.questions.some((q) => !q || typeof q.questionId !== 'string'
      || typeof q.questionKey !== 'string' || typeof q.applicable !== 'boolean' || typeof q.scored !== 'boolean'
      || (q.scored && !q.applicable) || (!q.scored && q.fixedScore !== null)
      || (q.optionCodes !== undefined && (!Array.isArray(q.optionCodes) || q.optionCodes.some((code) => typeof code !== 'string')))
      || !(q.fixedScore === null || (typeof q.fixedScore === 'number' && Number.isInteger(q.fixedScore) && q.fixedScore >= 1 && q.fixedScore <= 5)))) {
    throw new Error('questionnaire_policy_result_invalid');
  }
  if (expectedQuestions) {
    const expectedById = new Map(expectedQuestions.map((question) => [question.questionId, question.questionKey] as const));
    if (expectedById.size !== expectedQuestions.length || result.questions.length !== expectedById.size
      || new Set(result.questions.map((question) => question.questionId)).size !== expectedById.size
      || result.questions.some((question) => expectedById.get(question.questionId) !== question.questionKey)) {
      throw new Error('questionnaire_policy_result_incomplete');
    }
  }
  return result as QuestionnairePolicyEvaluation;
}
